/***********************
 * CONFIG
 ***********************/
const SHEET_ID = '1UBEgGLkRH_xhZREXowEVXUC_i9EDucn4a5DkFqCNR60';
const SHEET_TAB = 'DO NOT EDIT';
const SHEET_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${encodeURIComponent(SHEET_TAB)}`;

/***********************
 * STATE
 ***********************/
let map;
const markers = [];
let infoWindow;
const losAngeles = { lat: 34.063380, lng: -118.358080 };
window.stores = []; // single source of truth

/***********************
 * UTIL
 ***********************/
function escapeRegExp(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/***********************
 * LOAD SHEET → STORES
 ***********************/
async function fetchStoresFromSheet() {
  try {
    const res = await fetch(SHEET_URL);
    const text = await res.text();

    // Extract JSON payload from GViz wrapper safely
    const m = text.match(/google\.visualization\.Query\.setResponse\(([\s\S]*?)\);?$/);
    if (!m) throw new Error('Could not parse GViz response');
    const json = JSON.parse(m[1]);

    const cols = (json.table.cols || []).map(c => (c.label || '').trim());
    const rows = (json.table.rows || []).map(r => (r.c || []).map(c => (c ? c.v : '')));

    const getVal = (row, labelOptions) => {
      const labels = Array.isArray(labelOptions) ? labelOptions : [labelOptions];
      for (const label of labels) {
        const idx = cols.indexOf(label);
        if (idx !== -1) return row[idx] ?? '';
      }
      return '';
    };
    const toBool = v =>
      (typeof v === 'boolean') ? v : String(v).trim().toLowerCase() === 'true';
    const toNum = v => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    const parsed = rows.map(row => {
      const lat = toNum(getVal(row, 'Latitude'));
      const lng = toNum(getVal(row, 'Longitude'));
      const isGallery = toBool(getVal(row, ['Is Gallery', 'IsGallery', 'Gallery']));

      return {
        name: getVal(row, 'Name') || '',
        image: null,
        address: {
          street: getVal(row, 'Street') || '',
          city: getVal(row, 'City') || '',
          state: getVal(row, 'State') || '',
          country: getVal(row, 'Country') || '',
          postalCode: getVal(row, ['postalCode', 'Postal Code']) || '',
          latitude: lat,
          longitude: lng,
          mapLink: getVal(row, ['Map Link', 'MapLink']) || ''
        },
        phoneNumber: getVal(row, ['Phone', 'Phone Number']) || '',
        appointment: getVal(row, 'Appointment') || '',
        email: getVal(row, 'Email') || '',
        hours: getVal(row, 'Hours') || '',
        filters: getVal(row, 'Filters') || '',
        favorite: isGallery, // drives initial favorites
        isGallery
      };
    });

    // Keep only rows with a name + valid lat/lng
    window.stores = parsed.filter(s =>
      s.name &&
      s.address &&
      Number.isFinite(s.address.latitude) &&
      Number.isFinite(s.address.longitude)
    );

    // console.log(`✅ Loaded ${window.stores.length} valid rows from sheet`);
  } catch (err) {
    console.error('❌ fetchStoresFromSheet failed:', err);
    window.stores = [];
  }
}

/***********************
 * MAP + UI
 ***********************/
function setupMap(location) {
  const grayMapStyle = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
  ];

  map = new google.maps.Map(document.getElementById('map'), {
    center: location,
    zoom: 13,
    mapTypeId: 'roadmap',
    styles: grayMapStyle
  });

  infoWindow = new google.maps.InfoWindow();

  const searchInput = document.getElementById('zip-code');
  setSearchListener(searchInput);

  // initial render: show only favorites; if none, fallback to all
  searchStores(searchInput, true);
}

function clearLocations() {
  infoWindow && infoWindow.close();
  for (let i = 0; i < markers.length; i++) {
    markers[i].setMap(null);
  }
  markers.length = 0;
}

function showStoreMarkers(storesToShow) {
  const bounds = new google.maps.LatLngBounds();
  let added = false;

  for (let [index, store] of storesToShow.entries()) {
    const a = store.address || {};
    const addressHtml = `${a.street}, <br>${a.city}, ${a.state} ${a.country} ${a.postalCode}`;

    if (Number.isFinite(a.latitude) && Number.isFinite(a.longitude)) {
      const latlng = new google.maps.LatLng(a.latitude, a.longitude);
      createMarker(latlng, store.name, addressHtml, index);
      bounds.extend(latlng);
      added = true;
    }
  }

  if (added) {
    map.fitBounds(bounds);
  } else {
    map.setCenter(losAngeles);
    map.setZoom(3);
  }
}

function setActiveStoreContainer(containers, active) {
  containers.forEach(el => el.classList.remove('active'));
  active.classList.add('active');
}

function setOnClickListener() {
  const containers = document.querySelectorAll('.store-container');
  containers.forEach(function (storeEl, index) {
    storeEl.addEventListener('click', function () {
      setActiveStoreContainer(containers, storeEl);
      map.panTo(markers[index].position);
      google.maps.event.trigger(markers[index], 'click');
    });
  });
}

function setSearchListener(searchInput) {
  const btn = document.getElementById('storeLocatorTrigger');
  if (btn) {
    btn.addEventListener('click', () => searchStores(searchInput));
  }
  if (searchInput) {
    searchInput.addEventListener('keyup', (e) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        btn && btn.click();
      }
    });
  }
}

function searchStores(searchInput, initial = false) {
  let found = [];
  const term = (searchInput && searchInput.value) ? String(searchInput.value).trim() : '';

  const normalize = (s) =>
    String(s || '')
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip accents
      .trim()
      .replace(/\s+/g, ' ');

  const containsIgnoreCase = (value, term) =>
    !!term && new RegExp(escapeRegExp(normalize(term)), 'i').test(normalize(value));

  const equalsIgnoreCase = (value, term) =>
    !!term && new RegExp('^' + escapeRegExp(normalize(term)) + '$', 'i').test(normalize(value));


  if (term) {
    window.stores.forEach((store) => {
      if (
        containsIgnoreCase(store.name, term) ||
        containsIgnoreCase(store.address.country, term) ||
        containsIgnoreCase(store.address.city, term) ||
        containsIgnoreCase(store.address.state, term) ||
        containsIgnoreCase(store.address.street, term) ||
        containsIgnoreCase(store.filters, term) ||
        store.address.postalCode === term
      ) {
        found.push(store);
      }
    });
  } else if (initial) {
    found = window.stores.filter(s => s.favorite === true);
    if (found.length === 0) found = window.stores;
  } else {
    found = window.stores;
  }

  clearLocations();
  displayStores(found);
  showStoreMarkers(found);
  setOnClickListener();
}

function createMarker(latlng, name, addressHtml, index) {
  const containers = document.querySelectorAll('.store-container');
  const storeEl = document.getElementById('storeID' + index);

  const html = `
    <div class="marker__store-name">${name}</div>
    <div class="marker__store-location text-center">${addressHtml}</div>
  `;

  const marker = new google.maps.Marker({
    map,
    position: latlng,
    icon: 'https://cdn.shopify.com/s/files/1/1056/1394/files/pin.png?v=1707785563&width=20'
  });

  google.maps.event.addListener(marker, 'click', function () {
    infoWindow.setContent(html);
    infoWindow.open(map, marker);

    if (storeEl) {
      setActiveStoreContainer(containers, storeEl);
      storeEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  });

  markers.push(marker);
}

function displayStores(storesToShow) {
  const noResultsEl = document.getElementById('noResults');
  const listEl = document.querySelector('.stores-list');
  if (!listEl) return;

  if (storesToShow.length === 0) {
    noResultsEl && noResultsEl.removeAttribute('hidden');
    setTimeout(() => map.panTo(losAngeles), 500);
  } else {
    noResultsEl && noResultsEl.setAttribute('hidden', true);
  }

  let storesHtml = '';
  storesToShow.forEach(function (store, index) {
    const a = store.address || {};
    let address = `
      ${a.street || ''}<br/>
      ${a.city || ''} ${a.state || ''} ${a.country || ''} ${a.postalCode || ''}`.trim();

    if (a.mapLink) {
      address = `
        <a href="${a.mapLink}" target="_blank" rel="noopener">
          ${a.street || ''}<br/>
          ${a.city || ''} ${a.state || ''} ${a.country || ''} ${a.postalCode || ''}
        </a>
      `.trim();
    }

    const phoneHtml = store.phoneNumber
      ? `<li><a href="tel:${store.phoneNumber}" class="link-styled">${store.phoneNumber}</a></li>`
      : '';

    const apptHtml = store.appointment
      ? `<li><a href="${store.appointment}" class="store-info__appointment link-styled" target="_blank" rel="noopener">SCHEDULE AN APPOINTMENT</a></li>`
      : '';

    const emailHtml = store.email
      ? `<li><a href="mailto:${store.email}" target="_blank" rel="noopener" title="${store.email}">${store.email}</a></li>`
      : '';

    const hoursHtml = store.hours
      ? `<li><strong>Store hours:</strong><div class="store-hours">${store.hours}</div></li>`
      : '';

    const galleryIcon = `
      <div>
        <svg width="15" height="28" viewBox="0 0 15 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2483_7343)"><path d="M14.976 15.5171C14.9644 15.2483 14.9432 14.9566 14.9115 14.6345L14.9083 14.604C14.8765 14.2917 14.8416 13.9717 14.7866 13.6539C14.7135 13.2294 14.632 12.7919 14.5326 12.3555C14.304 11.3575 13.9716 10.3954 13.5409 9.49313C12.927 8.2067 12.2052 7.18365 11.3438 6.37935C10.823 5.89395 10.2515 5.50106 9.65145 5.217C9.05031 4.93185 8.31899 4.75989 7.70514 4.73268C7.38129 4.71853 7.04262 4.7305 6.61716 4.80778C6.08375 4.90573 5.55881 5.09292 5.05715 5.36175C4.31313 5.76008 3.65801 6.30426 3.03676 7.02257C2.53299 7.60484 2.07789 8.27962 1.69054 9.02514C1.17512 10.0155 0.779299 11.0745 0.511536 12.1705C0.351725 12.8246 0.240598 13.4242 0.161222 14.0011C0.121005 14.2928 0.0839625 14.6138 0.0543287 15.01C0.0257532 15.3985 0.00881959 15.7609 0.000352783 16.1821C0.000352783 16.2289 -0.00282227 16.7785 0.00987794 17.0115C0.0257532 17.2955 0.03422 17.5611 0.0543287 17.8342C0.0786707 18.1531 0.109363 18.4938 0.159105 18.8279C0.219431 19.2328 0.287166 19.6507 0.380301 20.1067C0.56022 20.9905 0.823749 21.8568 1.16136 22.6731C1.68631 23.9432 2.30544 24.9858 3.06534 25.851C3.58393 26.442 4.12157 26.9122 4.70049 27.2724C5.59479 27.8286 6.53567 28.1039 7.4395 28.1148C7.82156 28.1192 8.0036 28.093 8.30311 28.0484C8.7455 27.9831 9.20906 27.8329 9.64828 27.6283C10.5976 27.1854 11.4358 26.4899 12.2052 25.5278C12.7863 24.803 13.2742 23.9704 13.7113 22.9767C14.087 22.1223 14.3802 21.2081 14.5812 20.2819C14.6955 19.7574 14.7802 19.2839 14.8511 18.7506C14.8871 18.4796 14.9252 18.1444 14.9421 17.8832C14.9633 17.5447 14.9951 17.0365 14.9993 16.71C14.9993 16.6621 14.9993 16.6153 14.9993 16.5631C14.9993 16.2159 14.9929 15.8676 14.9771 15.5193L14.976 15.5171ZM12.8276 9.76086C12.9038 9.7848 12.9091 9.86316 12.9091 9.93608V12.7419C12.9091 12.9018 12.8657 12.9476 12.7133 12.9476H10.695C10.6865 12.9476 10.678 12.9476 10.6706 12.9476C10.6135 12.9476 10.569 12.9378 10.5468 12.891C10.5309 12.8583 10.55 12.7919 10.5743 12.7549C10.6632 12.621 10.7606 12.4937 10.8548 12.3642C11.4633 11.5283 12.0719 10.6925 12.6804 9.85664C12.7175 9.80548 12.7588 9.74018 12.8286 9.76195L12.8276 9.76086ZM10.5098 16.2246H8.34439C8.33063 16.2246 8.31687 16.2246 8.30417 16.2246C8.23961 16.2246 8.17505 16.2028 8.16341 16.1691C8.14224 16.1049 8.16764 16.0526 8.19728 16.0102C8.42482 15.6902 8.65766 15.3735 8.88944 15.0568C9.22705 14.5942 9.56572 14.1328 9.90016 13.6691C9.97636 13.5636 10.06 13.5157 10.1933 13.5157C11.04 13.5222 11.8877 13.5189 12.7344 13.5189C12.8667 13.5189 12.908 13.5614 12.908 13.6996C12.908 14.4756 12.908 15.2516 12.908 16.0276C12.908 16.1702 12.8551 16.2246 12.7154 16.2246C11.9798 16.2246 11.2443 16.2246 10.5087 16.2246H10.5098ZM12.4825 16.9886C11.893 17.9496 11.3025 18.9095 10.7119 19.8706C10.5987 20.0545 10.4854 20.2373 10.3743 20.4223C10.3224 20.5083 10.2568 20.5486 10.1542 20.5475C9.43025 20.5442 8.70529 20.5453 7.98137 20.5453C7.85225 20.5453 7.80992 20.5029 7.80992 20.3701C7.80992 19.236 7.80992 18.102 7.80992 16.9679C7.80992 16.8341 7.86601 16.7796 8.0036 16.7796C8.73069 16.7796 9.45777 16.7796 10.1849 16.7796H12.3174C12.3365 16.7796 12.3555 16.7796 12.3746 16.7796C12.5164 16.7829 12.5598 16.8634 12.4836 16.9886H12.4825ZM7.8258 9.20798V5.7122C7.8258 5.51738 7.87236 5.48255 8.06498 5.50432C8.96035 5.60445 9.87053 6.07679 10.6209 6.71239C11.3057 7.29248 11.856 7.98358 12.3238 8.75414C12.3704 8.82923 12.4169 8.90977 12.4614 8.98595C12.5153 9.07955 12.5259 9.16227 12.4614 9.25042C12.2719 9.50945 12.0846 9.76957 11.8962 10.0286C11.222 10.9537 10.5468 11.8788 9.87476 12.805C9.80385 12.9029 9.72024 12.9486 9.59959 12.9476C9.07677 12.9443 8.55394 12.9465 8.03112 12.9465C7.87554 12.9465 7.82685 12.8953 7.82685 12.7364V9.20689L7.8258 9.20798ZM7.80992 13.7029C7.80992 13.5581 7.84908 13.5189 7.98772 13.5189C8.36555 13.5189 8.74444 13.5178 9.12227 13.5211C9.16567 13.5211 9.23023 13.5407 9.24504 13.5712C9.26092 13.6038 9.24187 13.6702 9.21647 13.7061C8.81641 14.2623 8.41318 14.8162 8.00995 15.3702C7.99725 15.3876 7.98137 15.404 7.9655 15.4181C7.89565 15.4812 7.84061 15.4638 7.81733 15.3713C7.81098 15.3452 7.81098 15.3158 7.81098 15.2886C7.81098 15.0252 7.81098 14.7629 7.81098 14.4995C7.81098 14.2362 7.81098 13.9684 7.81098 13.7029H7.80992ZM7.95386 24.3654C7.91258 24.4318 7.81204 24.4014 7.81204 24.3219C7.81204 23.9083 7.80992 23.1215 7.80992 22.7688C7.80992 22.2845 7.80992 21.8002 7.80992 21.3159C7.80992 21.1624 7.85543 21.1156 8.0036 21.1156C8.56452 21.1156 9.12545 21.1134 9.68638 21.1178C9.74035 21.1178 9.82079 21.1396 9.83984 21.1777C9.85994 21.2168 9.83349 21.2974 9.80491 21.3442C9.3096 22.1583 8.81112 22.9702 8.31264 23.7832C8.21421 23.9432 8.11578 24.1031 8.01842 24.2642L7.95491 24.3654H7.95386ZM1.08622 12.9258C1.10845 12.7179 1.16242 12.5285 1.21111 12.3326C1.46299 11.3324 1.81225 10.3692 2.2811 9.45286C2.61765 8.7944 3.01136 8.18711 3.48021 7.62116C4.04008 6.94421 4.6899 6.37282 5.46885 5.97449C5.83821 5.7862 6.21922 5.64036 6.62351 5.55874C6.78015 5.52718 6.94736 5.49888 7.09765 5.48908C7.20243 5.48255 7.26593 5.54241 7.27016 5.65342C7.27228 5.72308 7.27016 5.79382 7.27016 5.86348V9.37558C7.27016 10.5749 7.27969 11.7732 7.26699 12.9715C7.25958 13.7105 7.02992 14.382 6.59599 14.9741C6.1663 15.5596 5.60749 15.9558 4.9111 16.1255C4.7079 16.1756 4.49517 16.1908 4.28561 16.2061C3.80406 16.2398 3.34051 16.1527 2.90235 15.9438C2.05884 15.54 1.48839 14.8848 1.20581 13.9695C1.09998 13.6278 1.04812 13.2784 1.08622 12.9258ZM2.20278 17.7526C2.27792 17.7309 2.31602 17.8147 2.35201 17.8723C2.89177 18.7169 3.43047 19.5625 3.96917 20.4082C4.07712 20.578 4.18613 20.7478 4.29302 20.9186C4.3724 21.046 4.35335 21.1189 4.21894 21.182C3.7829 21.3866 3.33416 21.551 2.8579 21.6206C2.67798 21.6467 2.49806 21.6718 2.31814 21.6859C2.18479 21.6957 2.13716 21.6402 2.13716 21.502C2.13716 20.9034 2.13716 20.3037 2.13716 19.7051C2.13716 19.1065 2.13716 18.5297 2.13716 17.9409C2.13716 17.8702 2.12023 17.7766 2.20172 17.7526H2.20278ZM3.59134 18.8334C3.2061 18.2304 2.82086 17.6285 2.43562 17.0256C2.42503 17.0093 2.41445 16.993 2.40492 16.9766C2.34248 16.8645 2.38587 16.7829 2.51182 16.7807C2.68539 16.7775 2.86002 16.7807 3.03464 16.7807H6.86587C7.04791 16.7807 7.09342 16.8319 7.07331 17.0191C7.00452 17.6514 6.83412 18.2533 6.55578 18.8214C6.26155 19.4211 5.86891 19.9446 5.37677 20.3864C5.26035 20.4909 5.13864 20.59 5.01588 20.6868C4.89311 20.7837 4.82855 20.7684 4.73859 20.628C4.35652 20.0305 3.9734 19.4319 3.59134 18.8344V18.8334ZM5.28152 21.1548C5.40746 21.0525 5.53658 20.9556 5.65829 20.849C6.25732 20.3266 6.72405 19.6986 7.06696 18.9737C7.09024 18.9237 7.12199 18.8769 7.15692 18.8355C7.1929 18.7931 7.23312 18.8029 7.24794 18.8573C7.26169 18.9052 7.2691 18.9563 7.2691 19.0053C7.27016 19.8738 7.2691 20.7423 7.2691 21.6097C7.2691 22.4772 7.2691 23.3348 7.2691 24.1978C7.2691 24.2664 7.28286 24.3567 7.20772 24.3807C7.13152 24.4046 7.09342 24.3197 7.05743 24.2631C6.48275 23.3642 5.91018 22.4652 5.33656 21.5662C5.30269 21.5129 5.2667 21.4606 5.2339 21.4051C5.16828 21.2952 5.17992 21.2353 5.28046 21.1537L5.28152 21.1548ZM7.20772 15.0513C7.22571 15.0198 7.27228 15.0328 7.27228 15.0687L7.2691 15.6663C7.2691 15.7892 7.2691 15.9122 7.2691 16.0352C7.26804 16.168 7.21407 16.2235 7.08601 16.2246C6.85529 16.2246 6.62351 16.2246 6.39279 16.2246C6.38432 16.2246 6.37586 16.2246 6.36845 16.2246C6.31659 16.2268 6.27743 16.2115 6.25732 16.1604C6.23933 16.1157 6.27743 16.0755 6.306 16.0494C6.43301 15.9285 6.57483 15.8208 6.69442 15.6935C6.82989 15.5498 6.94842 15.3887 7.07437 15.2353L7.20666 15.0502L7.20772 15.0513ZM1.59211 21.4443C1.59105 21.502 1.51379 21.515 1.49474 21.4606C1.49474 21.4606 1.44394 21.3333 1.42807 21.2843C1.33176 20.9774 1.24391 20.6814 1.17724 20.3734C1.07246 19.8923 0.960277 19.4276 0.895717 18.9215C0.856558 18.6157 0.813166 18.3088 0.786707 18.0008C0.758132 17.6623 0.734848 17.3227 0.725323 16.9821C0.721089 16.833 0.783532 16.7796 0.926409 16.7785C1.0894 16.7785 1.25344 16.7785 1.41643 16.7785C1.54131 16.7785 1.5974 16.8351 1.5974 16.9625C1.5974 18.3871 1.5974 19.8129 1.59635 21.2375L1.59211 21.4432V21.4443ZM1.94666 16.2224C1.76991 16.2289 1.59317 16.2257 1.41537 16.2257C1.24603 16.2257 1.07775 16.2257 0.908418 16.2257C0.781415 16.2257 0.716856 16.1734 0.721089 16.0417C0.732731 15.6467 0.745431 15.318 0.779299 14.9349C0.783532 14.8859 0.790941 14.8217 0.808932 14.776C0.829041 14.7216 0.878784 14.7129 0.920059 14.7509C0.94546 14.7749 0.968743 14.8043 0.985677 14.8358C1.2418 15.3049 1.57306 15.7065 1.99429 16.0265C1.99852 16.0298 2.00381 16.033 2.00699 16.0374C2.04614 16.0776 2.0726 16.0885 2.0726 16.1647C2.06414 16.2268 1.99005 16.2213 1.94772 16.2235L1.94666 16.2224ZM3.58499 25.05C3.38707 25.05 3.28441 24.9858 3.17646 24.8356C2.81345 24.3306 2.47372 23.7973 2.2049 23.2433C2.16574 23.1617 2.14563 23.0638 2.14034 22.9734C2.13081 22.7895 2.1361 22.6045 2.13822 22.4195C2.13928 22.3052 2.19325 22.2497 2.30226 22.2432C2.70867 22.2203 3.10555 22.1419 3.4982 22.0353C3.84957 21.9395 4.18401 21.8002 4.50893 21.6337C4.65815 21.5564 4.70895 21.5673 4.79997 21.7099C5.2339 22.3857 5.6657 23.0638 6.09751 23.7407C6.33035 24.1064 6.56424 24.4721 6.79602 24.8378C6.81084 24.8606 6.82565 24.8846 6.83518 24.9107C6.86587 24.9923 6.83624 25.0435 6.75263 25.05C6.69865 25.0544 6.64362 25.05 6.58964 25.05C6.1028 25.05 5.6149 25.05 5.12806 25.05H3.63367C3.61674 25.05 3.60086 25.05 3.58499 25.05ZM10.9638 25.8173C10.4156 26.3484 9.80597 26.7903 9.09687 27.0645C8.70529 27.2158 8.30205 27.3225 7.88401 27.3584C6.70924 27.4607 5.66253 27.0928 4.7153 26.3985C4.47718 26.2243 4.22846 26.0132 4.03584 25.8217C4.03055 25.8162 4.02103 25.8064 4.01573 25.801C3.94271 25.7281 3.98822 25.6214 4.08029 25.6214H7.50406H10.9204C11.0083 25.6214 11.0506 25.7313 10.9871 25.7944L10.9627 25.8184L10.9638 25.8173ZM12.9091 17.5774V20.2983C12.9091 21.1613 12.9112 22.0244 12.9069 22.8864C12.9069 22.9723 12.8784 23.0638 12.8413 23.14C12.5376 23.7679 12.1798 24.36 11.7618 24.914C11.6867 25.013 11.6464 25.0511 11.475 25.0511C11.4454 25.0511 11.4157 25.0489 11.385 25.0489C10.4315 25.0489 9.47788 25.0489 8.52431 25.0489H8.29888C8.23961 25.0489 8.20257 24.9814 8.23326 24.9292L8.26924 24.8672C8.32851 24.7551 8.39836 24.6473 8.46504 24.5396C9.63875 22.6263 10.8135 20.714 11.9883 18.8007C12.2232 18.4165 12.4593 18.0323 12.6953 17.6481L12.7799 17.499C12.7799 17.499 12.8053 17.4577 12.8508 17.4675C12.8508 17.4675 12.9091 17.4675 12.9091 17.5763V17.5774ZM13.5472 21.3344C13.5292 21.3888 13.4499 21.3757 13.4499 21.3181V21.0841C13.4478 19.7127 13.4478 18.3403 13.4488 16.969C13.4488 16.8373 13.5049 16.7818 13.634 16.7818C13.7833 16.7818 13.9335 16.7818 14.0828 16.7818C14.2076 16.7818 14.2764 16.8438 14.2712 16.969C14.2606 17.2389 14.2457 17.511 14.2278 17.7809C14.214 17.9844 14.1928 18.1858 14.1695 18.3882C14.141 18.6451 14.1082 18.8976 14.0637 19.1512C13.9812 19.6289 13.8806 20.1492 13.7727 20.566C13.7261 20.7445 13.6795 20.9252 13.6245 21.1015L13.5462 21.3355L13.5472 21.3344ZM13.6287 11.7362C13.7674 12.226 13.9018 12.7342 13.9886 13.2458C14.0457 13.5853 14.1029 13.9249 14.1484 14.2655C14.1812 14.5191 14.2045 14.7597 14.2267 15.0317C14.2532 15.3659 14.269 15.725 14.2764 16.033C14.2796 16.1702 14.2172 16.2246 14.0859 16.2257C13.9335 16.2257 13.7811 16.2257 13.6277 16.2257C13.5038 16.2257 13.4488 16.168 13.4488 16.0406C13.4488 14.6095 13.4488 13.1783 13.4499 11.7471V11.3749C13.4499 11.3477 13.489 11.3411 13.4996 11.3662C13.5303 11.4369 13.5748 11.5501 13.6287 11.7384V11.7362Z" fill="black"/><path d="M4.01148 2.16338C4.49726 2.34187 4.87509 2.66728 5.18519 3.08303C5.41908 3.39648 5.58313 3.7491 5.70907 4.12023C5.74506 4.22798 5.76305 4.23668 5.86465 4.19533C6.30492 4.01466 6.76319 3.90691 7.23521 3.88297C7.53896 3.86665 7.84271 3.87644 8.14645 3.92324C8.49042 3.97657 8.82168 4.07017 9.14554 4.1975C9.23973 4.23451 9.26301 4.22471 9.29476 4.12676C9.426 3.72734 9.60804 3.35512 9.86416 3.02426C10.1192 2.69558 10.423 2.42893 10.7934 2.24718C10.8632 2.21344 10.9363 2.18841 11.0082 2.15902C11.0664 2.13399 11.0918 2.09808 11.077 2.0491C11.0453 1.94026 11.0125 1.83143 10.9754 1.72368C10.9553 1.66491 10.9109 1.63553 10.8495 1.63335C10.7817 1.63008 10.714 1.62355 10.6452 1.62247C10.2462 1.61811 9.84616 1.61376 9.44611 1.61049C9.39425 1.61049 9.34874 1.63117 9.31911 1.6758C9.26513 1.7596 9.21115 1.8434 9.1593 1.92938C9.13072 1.97727 9.13389 2.02516 9.17411 2.0676C9.37414 2.27548 9.45563 2.52471 9.41859 2.8153C9.39213 3.01991 9.30429 3.19187 9.15612 3.32791C8.97091 3.49769 8.75289 3.56517 8.50418 3.54123C8.2724 3.51837 8.08824 3.4128 7.94113 3.23323C7.80143 3.06344 7.74851 2.86427 7.75592 2.64443C7.76439 2.4039 7.85647 2.20473 8.02792 2.04583C8.18667 1.89891 8.374 1.82163 8.59096 1.83034C8.64494 1.83252 8.69362 1.81728 8.72537 1.77048C8.78252 1.6845 8.83861 1.59635 8.89047 1.50601C8.92328 1.4516 8.90741 1.4135 8.84496 1.39827C8.77617 1.38194 8.70526 1.36888 8.63329 1.36344C8.4121 1.34711 8.2036 1.39718 8.00358 1.49513C7.93373 1.52996 7.87975 1.51907 7.83424 1.46792C7.79085 1.42003 7.7845 1.36562 7.81942 1.29596C7.86176 1.20889 7.90303 1.12182 7.95278 1.03911C7.99934 0.960749 7.99723 0.892183 7.95383 0.811645C7.81942 0.560236 7.69136 0.304474 7.55907 0.0519761C7.54637 0.0280324 7.52203 -0.00135306 7.50192 -0.00244141C7.48287 -0.00244141 7.45641 0.0269441 7.44371 0.0497994C7.3093 0.305562 7.17806 0.563501 7.04259 0.819263C7.00344 0.893271 7.00238 0.958572 7.04365 1.03149C7.0934 1.11856 7.13785 1.20889 7.1823 1.29923C7.21616 1.36779 7.2077 1.42439 7.1643 1.47119C7.1188 1.51907 7.06588 1.52778 6.99814 1.49731C6.98121 1.48969 6.96428 1.48098 6.94734 1.47336C6.6944 1.36017 6.43298 1.32643 6.16311 1.39718C6.09008 1.41568 6.07526 1.4516 6.11336 1.51472C6.16205 1.59526 6.21391 1.67362 6.26365 1.75416C6.29752 1.80857 6.34514 1.82925 6.40653 1.83143C6.47426 1.83469 6.54305 1.83469 6.60867 1.85102C7.08599 1.96747 7.36222 2.46376 7.20241 2.96876C7.06482 3.40301 6.62455 3.63047 6.19697 3.52055C5.82655 3.42478 5.57572 3.08739 5.57255 2.70102C5.57043 2.45723 5.65192 2.24609 5.8202 2.07196C5.87523 2.01536 5.87947 1.98706 5.83713 1.9185C5.79057 1.84122 5.73977 1.76613 5.69214 1.68886C5.65616 1.63008 5.6043 1.60614 5.53974 1.60832C5.43179 1.61158 5.13651 1.62029 4.81794 1.62029C4.49938 1.62029 4.38084 1.62029 4.1533 1.63226C4.0898 1.63553 4.04535 1.666 4.02524 1.72695C3.99349 1.81946 3.96491 1.91414 3.93528 2.00774C3.9067 2.09916 3.92046 2.12964 4.00936 2.16229L4.01148 2.16338Z" fill="black"/></g><defs><clipPath id="clip0_2483_7343"><rect width="15" height="28" fill="white"/></clipPath></defs></svg>
      </div>
    `;

    storesHtml += `
      <li id="storeID${index}" class="store-container flex ais jcb">
        <ul class="store-info-container list-unstyled">
          <li class="font-s"><strong><p>${store.name || ''}</p></strong></li>
          <li class="store-info__location">${address}</li>
          ${phoneHtml}
          ${apptHtml}
          ${emailHtml}
          ${hoursHtml}          
        </ul>
        ${store.isGallery ? galleryIcon : ''}
      </li>
    `;
  });

  listEl.innerHTML = storesHtml;
}

/***********************
 * BOOTSTRAP (single init)
 ***********************/
let _started = false;

async function bootstrap() {
  if (_started) return;
  _started = true;

  await fetchStoresFromSheet();
  setupMap(losAngeles);          // or initMap(losAngeles) if that’s your builder
}

window.initMap = () => {         // Maps API will call this
  bootstrap().catch(console.error);
};

// Optional fallback if someone removes &callback=initMap:
document.addEventListener('DOMContentLoaded', () => {
  if (window.google && window.google.maps && !_started) window.initMap();
});
